import React, { useState, useEffect, useCallback } from 'react';
import { Paper, 
  Box,
  Typography,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Button, 
  Stack, 
  Chip, 
  Grid, 
  CircularProgress,
  DialogActions
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AddBookingDocument from './AddBookingDocument';
import { useBookingDetails } from '../../../contexts/BookingDetailsContext';
import { fetchBookingDocumentTypeSelect } from '../../../services/common';
import { bookingTokens, cardSx, cardHeadSx, emptyStateSx, primaryButtonSx, ghostButtonSx } from './bookingDetailsTheme';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DeleteIcon from '@mui/icons-material/Delete';

const BookingDocumentsTab = ({ booking, onActionComplete }) => {
  const [addDocOpen, setAddDocOpen] = useState(false);
  const [documentTypeOptions, setDocumentTypeOptions] = useState([]);
  const { fetchBookingDocuments, deleteBookingDocument } = useBookingDetails();
  const [bookingDocuments, setBookingDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDoc, setDeleteDoc] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const bookingId = booking.id;

  const loadBookingDocuments = async () => {
    try {
      const response = await fetchBookingDocuments(bookingId);
      setBookingDocuments(response);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchDocumentTypeOptions = async () => {
      const response = await fetchBookingDocumentTypeSelect();
      setDocumentTypeOptions(response);
    };

    fetchDocumentTypeOptions();
    loadBookingDocuments();
  }, [bookingId]);

  useEffect(() => {
    if (addDocOpen) {
      setError('');
    }
  }, [addDocOpen]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper elevation={1} sx={{ p: 3, borderRadius: 3 }}>
        <Typography color="error">Error: {error}</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ ...cardSx }}>
      <Box sx={cardHeadSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography component="span" sx={{ fontSize: 14 }}>📄</Typography>
          <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: bookingTokens.ink }}>Booking Documents</Typography>
        </Box>
        {booking.status !== 'cancelled' && booking.status !== 'completed' && (
          <Button size="small" variant="contained" startIcon={<UploadIcon />} onClick={() => setAddDocOpen(true)} sx={primaryButtonSx}>Upload Document</Button>
        )}
      </Box>
      <TableContainer>
        <Table size="small" sx={{ '& thead tr': { background: '#f8fbf9' }, '& th': { padding: '10px 16px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.55px', color: bookingTokens.subtle, borderBottom: `1.5px solid ${bookingTokens.border}` } }}>
          <TableHead>
            <TableRow>
              <TableCell>Uploaded Date</TableCell>
              <TableCell>Document Type</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bookingDocuments && bookingDocuments.length > 0 ? (
              bookingDocuments.map((doc, index) => (
                <TableRow key={`${doc.title}-${index}`} sx={{ '&:hover td': { bgcolor: '#f2f8f4' } }}>
                  <TableCell sx={{ color: bookingTokens.muted }}>
                    {new Date(doc.uploaded_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </TableCell>
                  <TableCell>
                    <Box component="span" sx={{ bgcolor: bookingTokens.blueLt, color: bookingTokens.blue, border: '1px solid #93c5fd', fontSize: 11, fontWeight: 700, px: 1.125, py: 0.25, borderRadius: 99 }}>{doc.document_type.replace(/_/g, ' ').toUpperCase()}</Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875 }}>
                      <Typography component="span" sx={{ fontSize: 16 }}>📎</Typography>
                      <Typography component="a" href={doc.url} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 12.5, color: bookingTokens.green, fontWeight: 600, cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>{doc.title}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" startIcon={<DownloadIcon />} href={doc.url} target="_blank" download sx={ghostButtonSx}>View</Button>
                      <Button
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => setDeleteDoc({ url: doc.url, title: doc.title, document_type: doc.document_type })}
                        sx={{ ...ghostButtonSx, borderColor: bookingTokens.redBdr, color: bookingTokens.red }}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 0, border: 'none' }}>
                  <Box sx={emptyStateSx}>
                    <Typography sx={{ fontSize: 40, opacity: 0.4 }}>🗂️</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: bookingTokens.muted }}>No documents available.</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <AddBookingDocument
        open={addDocOpen}
        onClose={() => setAddDocOpen(false)}
        bookingId={bookingId}
        documentTypeOptions={documentTypeOptions}
        onSuccess={loadBookingDocuments}
      />
      <Dialog open={!!deleteDoc} onClose={() => setDeleteDoc(null)}>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <b>{deleteDoc?.title}</b>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDoc(null)} disabled={deleting}>Cancel</Button>
          <Button
            color="error"
            onClick={async () => {
              setDeleting(true);
              try {
                // Send document_type and url for deletion
                await deleteBookingDocument(bookingId, {
                  document_type: deleteDoc.document_type,
                  url: deleteDoc.url
                });
                setDeleteDoc(null);
                await loadBookingDocuments();
              } catch (err) {
                // Optionally show error
              } finally {
                setDeleting(false);
              }
            }}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default BookingDocumentsTab;